import type {
  IPermissionGroup,
  IPermissionGroupCreate,
  IPermissionGroupUpdate
} from "../entities/permission_group";
import { BaseOrganizationEndpoints } from "./base";
import { PaginatedResult } from "./response/paginated_result";

const BASE_PATH = "/v1/permission-groups";

export class PermissionGroupsClient extends BaseOrganizationEndpoints {
  async list(params: { cursor?: string; limit?: number } = {}) {
    const urlParams = this.formatGenericParams({
      cursor: params.cursor ?? null,
      limit: params.limit ?? null
    });
    const qs = urlParams.toString();
    return this.requestWithStatus<PaginatedResult<IPermissionGroup>>(
      "GET",
      qs ? `${BASE_PATH}?${qs}` : BASE_PATH
    );
  }

  async create(payload: IPermissionGroupCreate) {
    return this.requestWithStatus<IPermissionGroup>("POST", BASE_PATH, payload);
  }

  async get(id: string) {
    return this.requestWithStatus<IPermissionGroup>(
      "GET",
      `${BASE_PATH}/${id}`
    );
  }

  async update(id: string, payload: IPermissionGroupUpdate) {
    return this.requestWithStatus<IPermissionGroup>(
      "PATCH",
      `${BASE_PATH}/${id}`,
      payload
    );
  }

  async delete(id: string) {
    return this.requestWithStatus<null>("DELETE", `${BASE_PATH}/${id}`);
  }

  async addUsers(id: string, userIds: string[]) {
    return this.requestWithStatus<null>("POST", `${BASE_PATH}/${id}/users`, {
      user_id: userIds
    });
  }

  async removeUsers(id: string, userIds: string[]) {
    return this.requestWithStatus<null>("DELETE", `${BASE_PATH}/${id}/users`, {
      user_id: userIds
    });
  }

  async addTeams(id: string, teamIds: string[]) {
    return this.requestWithStatus<null>("POST", `${BASE_PATH}/${id}/teams`, {
      team_id: teamIds
    });
  }

  async removeTeams(id: string, teamIds: string[]) {
    return this.requestWithStatus<null>("DELETE", `${BASE_PATH}/${id}/teams`, {
      team_id: teamIds
    });
  }

  async addOffices(id: string, officeIds: string[]) {
    return this.requestWithStatus<null>("POST", `${BASE_PATH}/${id}/offices`, {
      office_id: officeIds
    });
  }

  async removeOffices(id: string, officeIds: string[]) {
    return this.requestWithStatus<null>(
      "DELETE",
      `${BASE_PATH}/${id}/offices`,
      { office_id: officeIds }
    );
  }

  async regenerateJoinCode(id: string) {
    return this.requestWithStatus<IPermissionGroup>(
      "POST",
      `${BASE_PATH}/${id}/join-code`
    );
  }

  getJoinUrl(joinCode: string): string {
    return this.getUrl(`signup?join=${encodeURIComponent(joinCode)}`);
  }
}
